import * as React from 'react'
import { theme } from '../../theme'
import { MainImageCanvas } from './MainImageCanvas'
import { DimensionOptions, AspectOption } from '../../dimension_management/DimensionOptions'
import { TDrawingTool } from '../../types'

// Define TextConfig type to match what's used in DisclaimerTextCanvasPreview
interface TextConfig {
	fontSize: number
	padding: number
	lineHeight: number
	textBoxHeight: number
	lines: string[]
	hasText?: boolean
	yOffset?: number
	linePositions?: number[]
}

interface Props {
	// Image canvas props
	containerRef?: React.RefObject<HTMLDivElement>
	selectedAction: string
	imageCanvasRef: React.RefObject<HTMLCanvasElement>
	maskCanvasRef: React.RefObject<HTMLCanvasElement>
	previewRef: React.RefObject<HTMLCanvasElement>
	transform: {
		scale: number
		position: { x: number; y: number }
		isDragging: boolean
	}
	currentTool: TDrawingTool
	brushSize: number
	handleMouseDown: (e: React.MouseEvent) => void
	handleMouseMove: (e: React.MouseEvent) => void
	handleMouseUp: (e: React.MouseEvent) => void
	startDrawing: (e: React.MouseEvent) => void
	draw: (e: React.MouseEvent) => void
	stopDrawing: () => void
	showPromptInput: boolean
	showInputAbove: boolean
	prompt: string
	onPromptChange: (value: string) => void
	currentImage?: { url?: string }
	imageSrc?: string
	placeholderImage: string
	text: string
	imageWidth: number
	imageHeight: number
	textConfig: TextConfig
	textAlignment: 'left' | 'center'
	isLoading: () => boolean
	getCurrentMessage: () => string | undefined
	getScaledDimensions: (width: number, height: number) => { width: number; height: number }
	onSetSelectedAction: (action: string) => void
	onImageUpload: (file: File) => void

	// Dimension options props
	selectedDimension: AspectOption
	aiSelectedDimension: AspectOption
	dimensionImages: Record<string, { url: string; width: number; height: number }>
	handleDimensionSelect: (dimension: AspectOption) => void
	handleAiDimensionChange: (dimension: AspectOption) => void
	registerImageRef: (dimensionId: string, element: HTMLImageElement | null) => void
	registerContainerRef: (dimensionId: string, element: HTMLDivElement | null) => void
}

export const ContentArea = ({
	// Image canvas props
	containerRef,
	selectedAction,
	imageCanvasRef,
	maskCanvasRef,
	previewRef,
	transform,
	currentTool,
	brushSize,
	handleMouseDown,
	handleMouseMove,
	handleMouseUp,
	startDrawing,
	draw,
	stopDrawing,
	showPromptInput,
	showInputAbove,
	prompt,
	onPromptChange,
	currentImage,
	imageSrc,
	placeholderImage,
	text,
	imageWidth,
	imageHeight,
	textConfig,
	textAlignment,
	isLoading,
	getCurrentMessage,
	getScaledDimensions,
	onSetSelectedAction,
	onImageUpload,

	// Dimension options props
	selectedDimension,
	aiSelectedDimension,
	dimensionImages,
	handleDimensionSelect,
	handleAiDimensionChange,
	registerImageRef,
	registerContainerRef,
}: Props) => {
	// Compute dimensions for the selected action
	const dimensionDisplayWidth =
		selectedAction === 'create' ? aiSelectedDimension.displayWidth : selectedDimension.displayWidth

	const dimensionDisplayHeight =
		selectedAction === 'create' ? aiSelectedDimension.displayHeight : selectedDimension.displayHeight

	return (
		<div
			style={{
				flex: 1,
				padding: theme.spacing.md,
				display: 'flex',
				flexDirection: 'column',
				gap: theme.spacing.md,
				minHeight: 0,
				overflow: 'hidden',
				borderRight: `1px solid ${theme.colors.border}`,
			}}
		>
			{/* Main Image Canvas */}
			<MainImageCanvas
				containerRef={containerRef}
				selectedAction={selectedAction}
				imageCanvasRef={imageCanvasRef}
				maskCanvasRef={maskCanvasRef}
				previewRef={previewRef}
				transform={transform}
				currentTool={currentTool}
				brushSize={brushSize}
				handleMouseDown={handleMouseDown}
				handleMouseMove={handleMouseMove}
				handleMouseUp={handleMouseUp}
				startDrawing={startDrawing}
				draw={draw}
				stopDrawing={stopDrawing}
				showPromptInput={showPromptInput}
				showInputAbove={showInputAbove}
				prompt={prompt}
				onPromptChange={onPromptChange}
				currentImage={currentImage}
				imageSrc={imageSrc}
				placeholderImage={placeholderImage}
				text={text}
				imageWidth={imageWidth}
				imageHeight={imageHeight}
				textConfig={textConfig}
				textAlignment={textAlignment}
				isLoading={isLoading}
				getCurrentMessage={getCurrentMessage}
				getScaledDimensions={getScaledDimensions}
				dimensionDisplayWidth={dimensionDisplayWidth}
				dimensionDisplayHeight={dimensionDisplayHeight}
				onSetSelectedAction={onSetSelectedAction}
				onImageUpload={onImageUpload}
			/>

			{/* Dimension Options with Preview Thumbnails */}
			<DimensionOptions
				selectedOption={selectedAction === 'create' ? aiSelectedDimension : selectedDimension}
				onSelect={(option) => {
					if (selectedAction === 'create') {
						handleAiDimensionChange(option)
					} else {
						handleDimensionSelect(option)
					}
				}}
				containerWidth={getScaledDimensions(dimensionDisplayWidth, dimensionDisplayHeight).width}
				showPreviews={true}
				dimensionImages={dimensionImages}
				defaultImageSrc={
					selectedAction === 'create' && !currentImage?.url
						? placeholderImage
						: currentImage?.url ?? imageSrc ?? placeholderImage
				}
				registerImageRef={registerImageRef}
				registerContainerRef={registerContainerRef}
				onSwitchToAdjustTool={() => onSetSelectedAction('adjust')}
			/>
		</div>
	)
}
